import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, X, Loader2, FileText, CornerDownLeft, BookOpen, MessageSquare, Folder, Layers, Image as ImageIcon, User,
} from 'lucide-react';
import apiClient from '../../services/apiClient';
import { routeFor, queryTerms, splitByTerms } from './searchUtils';

const DEBOUNCE_MS = 300;

// type -> row icon + filter-chip label. Order here is the chip order.
const TYPES = {
  story: { Icon: BookOpen, label: 'Stories' },
  file: { Icon: FileText, label: 'Files' },
  chat: { Icon: MessageSquare, label: 'Chats' },
  output: { Icon: Layers, label: 'Outputs' },
  project: { Icon: Folder, label: 'Projects' },
  asset: { Icon: ImageIcon, label: 'Assets' },
  entity: { Icon: User, label: 'Characters & more' },
};

const formatWhen = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
};

const Highlight = ({ text, terms, markBg }) => (
  <>
    {splitByTerms(text, terms).map((p, i) => (p.match
      ? <mark key={i} style={{ background: markBg, color: 'inherit', borderRadius: '2px', padding: 0 }}>{p.text}</mark>
      : <React.Fragment key={i}>{p.text}</React.Fragment>))}
  </>
);

/**
 * GlobalSearchModal — Cmd/Ctrl+K command palette. Searches everything the user
 * owns or has been shared (projects, files, chats, stories, outputs, assets,
 * characters) via GET /api/search — keyword + meaning-based — tags each hit
 * with its type, and navigates straight to it (see searchUtils.routeFor).
 * Stories/outputs open as read-only markdown in the Outputs dock. Mounted once
 * in App.js so it can overlay any route, matching the existing
 * window.__open*Modal() convention used by ReportIssueModal/AIFeedbackModal/
 * SettingsModal there.
 */
const GlobalSearchModal = ({ isOpen, onClose, isDarkMode = false }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState(null);
  const [results, setResults] = useState([]);
  const [semanticStatus, setSemanticStatus] = useState('ok');
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  // Reset on open, autofocus the input
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setTypeFilter(null);
    setResults([]);
    setSemanticStatus('ok');
    setSelectedIndex(0);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [isOpen]);

  const runSearch = useCallback(async (q, type) => {
    const requestId = ++requestIdRef.current;
    if (q.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.get(`/api/search?q=${encodeURIComponent(q)}${type ? `&types=${type}` : ''}`);
      if (requestId !== requestIdRef.current) return; // stale response, a newer query already fired
      setResults(res?.results || []);
      setSemanticStatus(res?.semantic || 'ok');
      setSelectedIndex(0);
    } catch (err) {
      if (requestId === requestIdRef.current) setResults([]);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query, typeFilter), DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [query, typeFilter, runSearch]);

  const handleSelect = useCallback((result) => {
    if (!result) return;
    onClose();
    navigate(routeFor(result));
  }, [navigate, onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    }
  }, [results, selectedIndex, onClose, handleSelect]);

  if (!isOpen) return null;

  const colors = {
    overlay: 'rgba(0,0,0,0.5)',
    bg: isDarkMode ? '#141414' : '#ffffff',
    border: isDarkMode ? '#2d2d2d' : '#e0e0e0',
    text: isDarkMode ? '#f0f0f0' : '#111',
    subtext: isDarkMode ? '#8a8a8a' : '#767676',
    hover: isDarkMode ? '#1f1f1f' : '#f5f5f5',
    inputBg: isDarkMode ? '#1a1a1a' : '#fafafa',
  };

  const terms = queryTerms(query);
  const markBg = isDarkMode ? 'rgba(250,204,21,0.28)' : 'rgba(250,204,21,0.45)';
  const filters = [[null, 'All'], ...Object.entries(TYPES).map(([key, v]) => [key, v.label])];

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100000,
        backgroundColor: colors.overlay,
        display: 'flex', justifyContent: 'center',
        paddingTop: '12vh',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width: '100%', maxWidth: '620px', maxHeight: '60vh',
          backgroundColor: colors.bg, border: `1px solid ${colors.border}`,
          borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          height: 'fit-content',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', borderBottom: `1px solid ${colors.border}` }}>
          <Search size={16} color={colors.subtext} style={{ flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search stories, files, chats, outputs and more…"
            style={{
              flex: 1, border: 'none', outline: 'none', background: 'none',
              fontSize: '14px', color: colors.text, fontFamily: 'inherit',
            }}
          />
          {loading && <Loader2 size={14} className="animate-spin" color={colors.subtext} />}
          <button
            onClick={onClose}
            aria-label="Close search"
            style={{ display: 'flex', border: 'none', background: 'none', cursor: 'pointer', color: colors.subtext, padding: 0 }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: '6px', padding: '8px 12px', overflowX: 'auto', borderBottom: `1px solid ${colors.border}` }}>
          {filters.map(([key, label]) => (
            <button
              key={label}
              onClick={() => setTypeFilter(key)}
              aria-pressed={typeFilter === key}
              style={{
                flexShrink: 0, fontSize: '11px', padding: '3px 9px', borderRadius: '999px', cursor: 'pointer',
                fontFamily: 'inherit', border: `1px solid ${colors.border}`,
                backgroundColor: typeFilter === key ? colors.text : 'transparent',
                color: typeFilter === key ? colors.bg : colors.subtext,
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {semanticStatus === 'unavailable' && query.trim().length >= 2 && (
          <div role="status" style={{ padding: '6px 16px', fontSize: '11px', color: colors.subtext, borderBottom: `1px solid ${colors.border}` }}>
            Meaning-based results are unavailable right now — showing keyword matches only.
          </div>
        )}

        <div style={{ overflowY: 'auto', padding: results.length ? '6px' : 0 }}>
          {!loading && query.trim().length >= 2 && results.length === 0 && (
            <div style={{ padding: '28px 16px', textAlign: 'center', color: colors.subtext, fontSize: '13px' }}>
              No matches for “{query}”.
            </div>
          )}
          {query.trim().length < 2 && (
            <div style={{ padding: '28px 16px', textAlign: 'center', color: colors.subtext, fontSize: '13px' }}>
              Type at least 2 characters to search.
            </div>
          )}
          {results.map((result, i) => {
            const { Icon } = TYPES[result.type] || TYPES.file;
            return (
              <button
                key={`${result.type}:${result.id}`}
                onClick={() => handleSelect(result)}
                onMouseEnter={() => setSelectedIndex(i)}
                style={{
                  display: 'flex', flexDirection: 'column', gap: '4px', width: '100%',
                  textAlign: 'left', padding: '10px 12px', border: 'none', borderRadius: '8px',
                  cursor: 'pointer', fontFamily: 'inherit',
                  backgroundColor: i === selectedIndex ? colors.hover : 'transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                  <Icon size={12} color={colors.subtext} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Highlight text={result.title} terms={terms} markBg={markBg} />
                  </span>
                  {result.tag && (
                    <span style={{ flexShrink: 0, fontSize: '10px', padding: '1px 6px', borderRadius: '999px', border: `1px solid ${colors.border}`, color: colors.subtext }}>
                      {result.tag}
                    </span>
                  )}
                  {i === selectedIndex && <CornerDownLeft size={11} color={colors.subtext} style={{ flexShrink: 0, marginLeft: 'auto' }} />}
                </div>
                {result.snippet && (
                  <div style={{ fontSize: '12px', color: colors.subtext, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Highlight text={result.snippet} terms={terms} markBg={markBg} />
                  </div>
                )}
                <div style={{ fontSize: '10px', color: colors.subtext, display: 'flex', gap: '6px' }}>
                  {result.projectName && <><span>{result.projectName}</span><span>·</span></>}
                  <span>{formatWhen(result.date)}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearchModal;
